<template>
  <div
    class="json-nested-block"
    :class="{
      'json-nested-block--compact': compact,
      'json-nested-block--nested': depth > 0,
      'json-nested-block--asset-detail': isAssetDetailTable,
    }"
  >
    <!-- 对象数组 → 主表 -->
    <template v-if="tableMode">
      <div v-if="sectionTitle" class="json-nested-section-title">{{ sectionTitle }}</div>
      <el-table
        :data="pagedRows"
        border
        stripe
        size="mini"
        class="json-nested-table"
        :class="{ 'json-nested-table--fit-content': isAssetDetailTable }"
        :max-height="tableMaxHeight"
      >
        <el-table-column
          v-for="col in tableColumns"
          :key="col"
          :prop="col"
          :label="colLabel(col)"
          :min-width="columnWidth(col)"
          :show-overflow-tooltip="!isAssetDetailTable"
        >
          <template slot-scope="scope">
            <json-nested-block
              v-if="isNested(scope.row[col])"
              :value="scope.row[col]"
              :depth="depth + 1"
              :compact="true"
              :field-labels="fieldLabels"
              :section-title="colLabel(col)"
            />
            <span v-else class="json-nested-primitive">{{ formatPrimitive(scope.row[col]) }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="allRows.length > pageSize" class="json-nested-pager">
        <span class="json-nested-pager-total">共 {{ allRows.length }} 条</span>
        <el-pagination
          small
          layout="prev, pager, next"
          :current-page.sync="currentPage"
          :page-size="pageSize"
          :total="allRows.length"
        />
      </div>
    </template>

    <!-- 普通对象 → 键值表 -->
    <template v-else-if="objectMode">
      <div v-if="sectionTitle" class="json-nested-section-title">{{ sectionTitle }}</div>
      <table class="json-nested-kv">
        <tbody>
          <tr v-for="entry in objectEntries" :key="entry.key">
            <th class="json-nested-kv-key">{{ colLabel(entry.key) }}</th>
            <td class="json-nested-kv-val">
              <json-nested-block
                v-if="isNested(entry.val)"
                :value="entry.val"
                :depth="depth + 1"
                :compact="true"
                :field-labels="fieldLabels"
              />
              <span v-else class="json-nested-primitive">{{ formatPrimitive(entry.val) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- 简单数组 -->
    <template v-else-if="primitiveArrayMode">
      <div v-if="sectionTitle" class="json-nested-section-title">{{ sectionTitle }}</div>
      <ul class="json-nested-list">
        <li v-for="(item, idx) in normalizedValue" :key="idx">{{ formatPrimitive(item) }}</li>
      </ul>
    </template>

    <!-- 原始值 -->
    <template v-else>
      <span class="json-nested-primitive">{{ formatPrimitive(normalizedValue) }}</span>
    </template>
  </div>
</template>

<script>
import {
  parseJsonValue,
  isTableArray,
  isPlainObject,
  isNestedCellValue,
  collectColumnKeys,
  formatPrimitive,
  fieldLabel,
  isAssetDetailTable as checkIsAssetDetailTable,
  calcColumnMinWidth,
  calcAssetsDataColumnWidth,
} from '@/utils/jsonNestedTable';

import JsonNestedBlock from './JsonNestedBlock.vue';

export default {
  name: 'JsonNestedBlock',
  components: { JsonNestedBlock },
  props: {
    value: { default: null },
    depth: { type: Number, default: 0 },
    compact: { type: Boolean, default: false },
    sectionTitle: { type: String, default: '' },
    fieldLabels: { type: Object, default: () => ({}) },
    maxNestedTableHeight: { type: Number, default: 360 },
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 30,
    };
  },
  computed: {
    normalizedValue() {
      return parseJsonValue(this.value);
    },
    tableMode() {
      return isTableArray(this.normalizedValue);
    },
    objectMode() {
      return isPlainObject(this.normalizedValue);
    },
    primitiveArrayMode() {
      return (
        Array.isArray(this.normalizedValue) &&
        this.normalizedValue.length > 0 &&
        !this.tableMode
      );
    },
    allRows() {
      return this.tableMode ? this.normalizedValue : [];
    },
    tableColumns() {
      return collectColumnKeys(this.allRows);
    },
    pagedRows() {
      if (this.allRows.length <= this.pageSize) return this.allRows;
      const start = (this.currentPage - 1) * this.pageSize;
      return this.allRows.slice(start, start + this.pageSize);
    },
    objectEntries() {
      if (!this.objectMode) return [];
      return Object.keys(this.normalizedValue).map((key) => ({
        key,
        val: this.normalizedValue[key],
      }));
    },
    tableMaxHeight() {
      if (this.isAssetDetailTable) return Math.min(this.maxNestedTableHeight, 420);
      if (this.depth > 0) return Math.min(this.maxNestedTableHeight, 320);
      return this.maxNestedTableHeight;
    },
    isAssetDetailTable() {
      return checkIsAssetDetailTable(this.allRows);
    },
  },
  watch: {
    value() {
      this.currentPage = 1;
    },
  },
  methods: {
    isNested: isNestedCellValue,
    formatPrimitive,
    colLabel(key) {
      return fieldLabel(key, this.fieldLabels);
    },
    columnWidth(col) {
      if (col === 'assets_data') {
        return calcAssetsDataColumnWidth(this.allRows);
      }
      return calcColumnMinWidth(col, this.allRows, {
        isAssetDetail: this.isAssetDetailTable,
      });
    },
  },
};
</script>

<style lang="less">
.json-nested-block--nested {
  margin-top: 6px;
}

.json-nested-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px;
  padding-left: 8px;
  border-left: 3px solid #409eff;
}

.json-nested-block--nested .json-nested-section-title {
  font-size: 12px;
  margin-bottom: 6px;
}

.json-nested-table {
  width: 100%;
}

/* 资产明细：按内容撑开，避免 SN/PN 被压扁 */
.json-nested-table--fit-content {
  width: max-content;
  min-width: 100%;
}

.json-nested-block--asset-detail {
  overflow-x: auto;
  max-width: 100%;
}

.json-nested-block--nested.json-nested-block--asset-detail .json-nested-table th .cell,
.json-nested-block--nested.json-nested-block--asset-detail .json-nested-table td .cell {
  white-space: nowrap;
  padding-left: 12px;
  padding-right: 12px;
}

.json-nested-pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.json-nested-pager-total {
  font-size: 12px;
  color: #909399;
}

.json-nested-kv {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

.json-nested-kv th,
.json-nested-kv td {
  border: 1px solid #ebeef5;
  padding: 8px 12px;
  vertical-align: top;
}

.json-nested-kv-key {
  width: 140px;
  background: #f5f7fa;
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
}

.json-nested-kv-val {
  color: #303133;
  word-break: break-word;
}

.json-nested-list {
  margin: 0;
  padding-left: 20px;
  color: #606266;
  font-size: 13px;
}

.json-nested-primitive {
  word-break: break-word;
}
</style>
